/**
 * Instant Bank Discount (IBD) specific form state sub-hook.
 * Manages: bank names, instruments, event type, T&C, budget,
 * number of times incentivized, and guardrail fields.
 */

import { useState, useEffect, useMemo } from 'react';
import { MultiselectProps } from '@amzn/awsui-components-core/multiselect';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { EventType, InstrumentType, CampaignType } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { FormMode } from 'src/components/organisms/CampaignForm/types';
import { CampaignData } from 'src/@types/valueMatching/campaign';

export interface UseInstantBankDiscountFormStateParams {
    mode: FormMode;
    initialData?: CampaignData;
    config?: CampaignConfigurationData | null;
    campaignType?: CampaignType;
}

/** Instrument options — constant defined outside the component to avoid re-creation. */
const INSTRUMENT_OPTIONS: MultiselectProps.Option[] = Object.values(InstrumentType).map(
    (value) => ({ label: value, value }),
);

/** Event type options — constant defined outside the component to avoid re-creation. */
const EVENT_TYPE_OPTIONS: SelectProps.Option[] = Object.values(EventType).map(
    (value) => ({ label: value, value }),
);

export function useInstantBankDiscountFormState({ mode, initialData, config, campaignType }: UseInstantBankDiscountFormStateParams) {
    // ── IBD Attributes ──────────────────────────────────────────────────
    const [ibdBankNames, setIbdBankNames] = useState<MultiselectProps.Option[]>([]);
    const [ibdInstruments, setIbdInstruments] = useState<MultiselectProps.Option[]>([]);
    const [ibdEventType, setIbdEventType] = useState('');
    const [ibdTermsAndConditions, setIbdTermsAndConditions] = useState('');
    const [ibdCampaignBudget, setIbdCampaignBudget] = useState('');
    const [ibdMaxRewardValuePerTransaction, setIbdMaxRewardValuePerTransaction] = useState('');
    const [ibdMaxRewardValuePerPromotion, setIbdMaxRewardValuePerPromotion] = useState('');

    // ── Discount Guardrails ──────────────────────────────────────────────────
    const [ibdPercentExtentOfCompetitorGuardrail, setIbdPercentExtentOfCompetitorGuardrail] = useState('');
    const [ibdPercentOfASINPriceGuardrail, setIbdPercentOfASINPriceGuardrail] = useState('');
    const [ibdAbsoluteValueGuardrail, setIbdAbsoluteValueGuardrail] = useState('');
    const [ibdMaxTimesRewardAvailable, setIbdMaxTimesRewardAvailable] = useState('');

    // ── Populate from initialData (modify / view) ───────────────────────
    useEffect(() => {
        if (mode === FormMode.CREATE || !initialData) return;

        const ibdAttributes = initialData.attributes?.instantBankDiscount;
        if (ibdAttributes) {
            if (Array.isArray(ibdAttributes.bankNames)) {
                const availableBanks = config?.campaignTypeConfiguration?.instantBankDiscount?.availableBankNames;
                setIbdBankNames(
                    ibdAttributes.bankNames.map((code) => ({
                        label: availableBanks?.[code] ?? code,
                        value: code,
                    })),
                );
            }
            if (Array.isArray(ibdAttributes.instruments)) {
                setIbdInstruments(
                    ibdAttributes.instruments.map((instrument) => ({ label: instrument, value: instrument })),
                );
            }
            setIbdEventType(ibdAttributes.eventType ?? '');
            setIbdTermsAndConditions(ibdAttributes.termsAndConditions ?? '');
            setIbdCampaignBudget(String(ibdAttributes.campaignBudget ?? ''));
        }

        const ibdGuardrails = initialData.guardrails?.instantBankDiscount;
        if (ibdGuardrails) {
            setIbdPercentExtentOfCompetitorGuardrail(
                String(ibdGuardrails.percentExtentOfCompetitorGuardrail ?? ''),
            );
            setIbdPercentOfASINPriceGuardrail(
                String(ibdGuardrails.percentOfASINPriceGuardrail ?? ''),
            );
            setIbdAbsoluteValueGuardrail(
                String(ibdGuardrails.absoluteValueGuardrail?.currencyValue ?? ''),
            );
            setIbdMaxTimesRewardAvailable(String(ibdGuardrails.maxTimesRewardAvailable ?? ''));
            setIbdMaxRewardValuePerTransaction(String(ibdGuardrails.maxRewardValuePerTransaction ?? ''));
            setIbdMaxRewardValuePerPromotion(String(ibdGuardrails.maxRewardValuePerPromotion ?? ''));
        }
    // config is intentionally excluded from deps — the loading gate in
    // ModifyCampaignPage/CreateCampaignPage guarantees config is resolved before
    // initialData is populated, matching the useNcemiFormState pattern.
    }, [mode, initialData]);

    // ── Derive bank name options from config ────────────────────────────
    const ibdBankNameOptions: MultiselectProps.Option[] = useMemo(() => {
        if (campaignType !== CampaignType.INSTANT_BANK_DISCOUNT) return [];
        const availableBanks = config?.campaignTypeConfiguration?.instantBankDiscount?.availableBankNames;
        // Validate that availableBankNames is a non-null object before iterating
        if (availableBanks && typeof availableBanks === 'object' && !Array.isArray(availableBanks)) {
            return Object.entries(availableBanks).map(([code, displayName]) => ({
                label: displayName,
                value: code,
            }));
        }
        // No bank names available from config — return empty.
        // The validator will block submission until at least one bank is selected.
        return [];
    }, [config, campaignType]);

    return {
        // Attributes
        ibdBankNames,
        setIbdBankNames,
        ibdInstruments,
        setIbdInstruments,
        ibdEventType,
        setIbdEventType,
        ibdTermsAndConditions,
        setIbdTermsAndConditions,
        ibdCampaignBudget,
        setIbdCampaignBudget,
        ibdMaxRewardValuePerTransaction,
        setIbdMaxRewardValuePerTransaction,
        ibdMaxRewardValuePerPromotion,
        setIbdMaxRewardValuePerPromotion,

        // Guardrails
        ibdPercentExtentOfCompetitorGuardrail,
        setIbdPercentExtentOfCompetitorGuardrail,
        ibdPercentOfASINPriceGuardrail,
        setIbdPercentOfASINPriceGuardrail,
        ibdAbsoluteValueGuardrail,
        setIbdAbsoluteValueGuardrail,
        ibdMaxTimesRewardAvailable,
        setIbdMaxTimesRewardAvailable,

        // Options (static)
        ibdInstrumentOptions: INSTRUMENT_OPTIONS,
        ibdEventTypeOptions: EVENT_TYPE_OPTIONS,
        ibdBankNameOptions,
    };
}
